import { useCallback, useEffect, useState } from 'react';

import {
  CalendarIcon,
  ClockIcon,
  GlobeIcon,
  ZapIcon,
  InfoIcon,
  ChartNoAxesCombinedIcon,
} from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { selectAccountSlug, selectCampaignId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { trackCampaignLaunched, trackCampaignLaunchFailed } from '@/segment/segmentCampaignLaunch';
import useSegments from '@/segment/useSegments';
import { Campaign } from '@/shared/types/campaigns';
import { EventName, PageName } from '@/shared/types/segment';

interface SchedulingFormData {
  isScheduled: boolean;
  selectedDate?: Date;
  selectedTime: string;
  selectedTimezone: string;
}

import { ReviewSummary } from './components/ReviewSummary';
import { SummaryItemSkeleton, TextSkeleton } from '../../components/CampaignLoadingState';
import { useCampaigns } from '../../hooks/useCampaigns';
import { useCampaignSenders } from '../../hooks/useCampaignSenders';
import { useRegisterWizardStep } from '../../hooks/useWizardStepController';
import { selectDraftCampaignName, selectDraftAiAgentData } from '../../redux/selectors';
import {
  COMMON_TIMEZONES,
  getUserTimezone,
  formatCampaignDate,
  calculateScheduledStartTime,
  restoreScheduleFromCampaign,
  isScheduledTimeInFuture,
  getScheduledTimeMessage,
  parseCampaignSendersChain,
} from '../../utils';

/**
 * Non-agentic campaign Review page - displays a summary of campaign settings and lets
 * users configure final sending options before launching. Rendered (via the unified
 * Review delegator) for one-shot, non-agentic campaigns of any type.
 * @returns JSX element for the non-agentic campaign review page
 */
export const NonAgenticReview = () => {
  const { t } = useTranslation();
  const campaignId = useSelector(selectCampaignId);
  const slug = useSelector(selectAccountSlug);
  const navigate = useNavigate();

  // Redux selectors
  const draftCampaignName = useSelector(selectDraftCampaignName);
  const draftAiAgentData = useSelector(selectDraftAiAgentData);

  // Hooks
  const { campaign, updateCampaignAsync } = useCampaigns(campaignId);
  const { campaign_senders } = useCampaignSenders(campaignId);

  // audienceStats removed - now using campaign.subscribers_count directly

  // Parse senders to surface the primary channel (used for analytics tracking)
  const { primarySender } = parseCampaignSendersChain(campaign_senders);

  // Form for scheduling
  const form = useForm<SchedulingFormData>({
    defaultValues: {
      isScheduled: false,
      selectedDate: undefined,
      selectedTime: '',
      selectedTimezone: getUserTimezone(),
    },
  });

  const { control, setValue, clearErrors, setError } = form;
  const isScheduled = useWatch({ control, name: 'isScheduled' });
  const selectedDate = useWatch({ control, name: 'selectedDate' });
  const selectedTime = useWatch({ control, name: 'selectedTime' });
  const selectedTimezone = useWatch({ control, name: 'selectedTimezone' });

  // State for confirmation dialog
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  useSegments(PageName.CAMPAIGN_REVIEW);

  // Populate form with existing campaign data
  useEffect(() => {
    // Try to restore from campaign schedule with timezone from metadata
    const restoredSchedule = restoreScheduleFromCampaign(
      campaign?.schedule?.start_at,
      campaign?.metadata?.start_at_tz as string
    );

    if (restoredSchedule) {
      setValue('isScheduled', true);
      setValue('selectedDate', restoredSchedule.selectedDate);
      setValue('selectedTime', restoredSchedule.selectedTime);
      setValue('selectedTimezone', restoredSchedule.selectedTimezone);
    }
  }, [campaign?.schedule, campaign?.metadata, setValue]);

  // Get scheduled start time for campaign
  const getScheduledStartTime = useCallback((): number | 'now' => {
    return calculateScheduledStartTime(isScheduled, selectedDate, selectedTime, selectedTimezone);
  }, [isScheduled, selectedDate, selectedTime, selectedTimezone]);

  // Handle toggle change
  const handleScheduleToggle = (checked: boolean) => {
    trackEvent(EventName.CampaignToggleSchedule, {
      campaign_id: campaignId,
      is_scheduled: checked,
    });
    setValue('isScheduled', checked);
    if (!checked) {
      clearErrors(['selectedDate', 'selectedTime', 'selectedTimezone']);
    }
  };

  // Check if essential data is still loading
  const isLoadingEssentialData = !draftCampaignName || !draftAiAgentData?.brain_parent_id;

  /**
   * Common logic for saving campaign data - creates/updates campaign, uploads subscribers, and creates senders
   * @returns The created or updated campaign
   */
  const saveCampaignData = useCallback(async (): Promise<Campaign> => {
    // Calculate scheduled start time and prepare metadata
    const scheduledStartTime = getScheduledStartTime();
    let campaignMetadata: Record<string, unknown> = {};

    // If scheduled, save timezone in metadata so we can restore the form later
    if (isScheduled && selectedTimezone) {
      campaignMetadata = { start_at_tz: selectedTimezone };
    }

    const finalCampaign = await updateCampaignAsync({
      campaign_id: campaignId,
      schedule: { start_at: scheduledStartTime === 'now' ? 'now' : scheduledStartTime.toString() },
      metadata: campaignMetadata,
    });

    return finalCampaign;
  }, [campaignId, getScheduledStartTime, isScheduled, selectedTimezone, updateCampaignAsync]);

  const handleSaveAndExit = useCallback(async () => {
    try {
      const finalCampaign = await saveCampaignData();
      toast.success(t('campaigns.updated', { 0: finalCampaign.name }));

      // Note: We don't set status to 'ready' here - campaign remains in draft state
      navigate(`/${slug}/campaigns`);
    } catch (error) {
      console.error('Error saving campaign:', error);
    }
  }, [saveCampaignData, navigate, slug, t]);

  /**
   * Validates the scheduling form before showing confirmation dialog
   */
  const validateSchedulingForm = useCallback(
    (data: SchedulingFormData) => {
      if (!data.isScheduled) {
        return true; // No validation needed if not scheduled
      }

      let isValid = true;

      // Validate required fields when scheduled
      if (!data.selectedDate) {
        setError('selectedDate', { type: 'manual', message: 'Please select a date' });
        isValid = false;
      }

      if (!data.selectedTime) {
        setError('selectedTime', { type: 'manual', message: 'Please select a time' });
        isValid = false;
      }

      if (!data.selectedTimezone) {
        setError('selectedTimezone', { type: 'manual', message: 'Please select a timezone' });
        isValid = false;
      }

      // Check if the selected date/time is in the future
      if (data.selectedDate && data.selectedTime && data.selectedTimezone) {
        if (!isScheduledTimeInFuture(data.selectedDate, data.selectedTime, data.selectedTimezone)) {
          setError('selectedTime', {
            type: 'manual',
            message: 'Scheduled time must be in the future',
          });
          isValid = false;
        }
      }

      return isValid;
    },
    [setError]
  );

  /**
   * Shows confirmation dialog after validation
   */
  const handleReviewAndStart = useCallback(async () => {
    // Get current form data
    const formData = { isScheduled, selectedDate, selectedTime, selectedTimezone };

    // Validate first so a blocked (invalid-schedule) attempt isn't tracked.
    if (!validateSchedulingForm(formData)) {
      return;
    }

    trackEvent(EventName.CampaignReviewAndStart, {
      campaign_id: campaignId,
      campaign_type: campaign?.type || 'marketing',
      is_scheduled: isScheduled,
      subscribers_count: campaign?.subscribers_count || 0,
      channel_type: primarySender?.integration_type || '',
    });

    // Show confirmation dialog
    setIsConfirmDialogOpen(true);
  }, [
    isScheduled,
    selectedDate,
    selectedTime,
    selectedTimezone,
    validateSchedulingForm,
    campaignId,
    campaign?.type,
    campaign?.subscribers_count,
    primarySender?.integration_type,
  ]);

  /**
   * Actually submits the campaign after confirmation
   */
  const handleConfirmSubmit = useCallback(async () => {
    trackEvent(EventName.CampaignConfirmLaunch, {
      campaign_id: campaignId,
      campaign_type: campaign?.type || 'marketing',
      is_scheduled: isScheduled,
    });
    try {
      setIsConfirmDialogOpen(false);

      const finalCampaign = await saveCampaignData();
      await updateCampaignAsync({ campaign_id: finalCampaign.campaign_id, status: 'ready' });
      trackCampaignLaunched({
        campaign_id: finalCampaign.campaign_id,
        campaign_type: campaign?.type || 'marketing',
        is_scheduled: isScheduled,
      });
      toast.success(t('campaigns.updated', { 0: finalCampaign.name }));

      navigate(`/${slug}/campaigns`);
    } catch (error) {
      console.error('Error creating campaign:', error);
      trackCampaignLaunchFailed(error, {
        campaign_id: campaignId,
        campaign_type: campaign?.type || 'marketing',
        is_scheduled: isScheduled,
      });
      toast.error(t('campaigns.error_creating'));
    }
  }, [
    saveCampaignData,
    updateCampaignAsync,
    navigate,
    slug,
    t,
    campaignId,
    campaign?.type,
    isScheduled,
  ]);

  // The wizard "Review and start" CTA validates scheduling + opens the launch
  // dialog (handleReviewAndStart already does both, with tracking).
  useRegisterWizardStep(
    { complete: handleReviewAndStart, saveAndExit: handleSaveAndExit },
    isLoadingEssentialData
  );

  // Show loading state while essential data is loading
  if (isLoadingEssentialData) {
    return (
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Summary Section */}
        <div className="flex-1 space-y-6">
          <div className="p-6">
            <h3 className="mb-4 text-lg font-semibold">Summary</h3>

            <div className="space-y-4">
              {/* Audience */}
              <SummaryItemSkeleton />

              {/* Channel */}
              <SummaryItemSkeleton />

              {/* If not delivered */}
              <SummaryItemSkeleton />

              {/* Response handling */}
              <SummaryItemSkeleton />
            </div>
          </div>
        </div>

        {/* Sending Options Section */}
        <div className="w-full space-y-6 lg:w-125">
          <div className="rounded-lg border border-border bg-background p-6">
            <h3 className="mb-4 text-lg font-semibold">{t('campaigns.review.sending_options')}</h3>

            <div className="space-y-4">
              {/* Schedule for later */}
              <div className="flex items-center justify-between">
                <TextSkeleton className="h-4 w-32" />
                <div className="h-6 w-11 rounded-full bg-muted" />
              </div>

              {/* Date, Time, Timezone skeletons */}
              <div className="space-y-4">
                <div>
                  <TextSkeleton className="mb-2 h-4 w-8" />
                  <div className="h-10 w-full rounded-md bg-muted" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <TextSkeleton className="mb-2 h-4 w-8" />
                    <div className="h-10 w-full rounded-md bg-muted" />
                  </div>
                  <div>
                    <TextSkeleton className="mb-2 h-4 w-16" />
                    <div className="h-10 w-full rounded-md bg-muted" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Review and start button skeleton */}
          <div className="h-10 w-full rounded-md bg-muted" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4 rounded-xl bg-muted p-1 lg:flex-row">
        {/* Summary Section */}
        <ReviewSummary
          campaignType={campaign?.type || 'marketing'}
          subscriberCount={campaign?.subscribers_count || 0}
          campaignSenders={campaign_senders}
          aiAgentName={draftAiAgentData?.brain_name}
          aiAgentVersion={draftAiAgentData?.brain_version}
          isNBA={false}
        />

        {/* Sending Options Section */}
        <div className="w-full space-y-4 lg:w-125">
          <div className="rounded-lg border border-border bg-background p-6">
            <h3 className="mb-4 text-lg font-semibold">{t('campaigns.review.sending_options')}</h3>

            <Form {...form}>
              <div className="space-y-4">
                {/* Start */}
                <div className="space-y-2">
                  <span className="block text-sm font-medium">{t('campaigns.review.start')}</span>
                  <Select
                    value={isScheduled ? 'scheduled' : 'immediately'}
                    onValueChange={(value) => handleScheduleToggle(value === 'scheduled')}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediately">
                        {t('campaigns.review.immediately')}
                      </SelectItem>
                      <SelectItem value="scheduled">
                        {t('campaigns.review.scheduled_for')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date, Time, Timezone - only show when scheduled */}
                {isScheduled && (
                  <div className="space-y-4">
                    {/* Date */}
                    <FormField
                      control={control}
                      name="selectedDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date</FormLabel>
                          <FormControl>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  id="date-picker"
                                  variant="outline-neutral"
                                  className="w-full justify-between px-3 font-normal hover:bg-transparent"
                                >
                                  {field.value ? formatCampaignDate(field.value) : 'Select date'}
                                  <CalendarIcon />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={field.value}
                                  onSelect={(date) => {
                                    field.onChange(date);
                                    setValue('selectedDate', date);
                                    clearErrors('selectedDate');
                                  }}
                                  disabled={(date) => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    const compareDate = new Date(date);
                                    compareDate.setHours(0, 0, 0, 0);
                                    return compareDate < today;
                                  }}
                                />
                              </PopoverContent>
                            </Popover>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Time and Timezone */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Time */}
                      <FormField
                        control={control}
                        name="selectedTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Time</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  id="time-input"
                                  type="time"
                                  {...field}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    clearErrors('selectedTime');
                                  }}
                                  className="pr-10"
                                />
                                <ClockIcon className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Timezone */}
                      <FormField
                        control={control}
                        name="selectedTimezone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Timezone</FormLabel>
                            <FormControl>
                              <Select
                                value={field.value}
                                onValueChange={(value) => {
                                  field.onChange(value);
                                  setValue('selectedTimezone', value);
                                  clearErrors('selectedTimezone');
                                }}
                              >
                                <SelectTrigger id="timezone-select" className="[&>svg]:hidden">
                                  <SelectValue />
                                  <GlobeIcon className="ml-auto block! h-4 w-4" />
                                </SelectTrigger>
                                <SelectContent>
                                  {COMMON_TIMEZONES.map((tz) => (
                                    <SelectItem key={tz.value} value={tz.value}>
                                      {tz.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Show scheduled send time message */}
              {getScheduledTimeMessage(
                isScheduled,
                selectedDate,
                selectedTime,
                selectedTimezone
              ) && (
                <div className="mt-2 px-3 py-2 text-center">
                  <p className="text-sm leading-5 text-muted-foreground">
                    <span>{t('campaigns.review.send_time')}:</span>{' '}
                    <span className="font-medium">
                      {getScheduledTimeMessage(
                        isScheduled,
                        selectedDate,
                        selectedTime,
                        selectedTimezone
                      )}
                    </span>{' '}
                    <span>{t('campaigns.review.from_now')}</span>
                  </p>
                </div>
              )}
            </Form>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              {t('campaigns.review.dialog.title')}
            </DialogTitle>
          </DialogHeader>

          <DialogDescription asChild>
            <>
              <div className="space-y-4 px-4">
                {/* Scheduled time info - different message for immediate vs scheduled */}
                {isScheduled && selectedDate && selectedTime ? (
                  <div className="flex items-center gap-3">
                    <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <p className="text-sm text-foreground">
                      {t('campaigns.review.dialog.scheduled_message', {
                        0: selectedTime,
                        1: formatCampaignDate(selectedDate),
                      })}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <p className="text-sm text-foreground">
                      {t('campaigns.review.dialog.immediate_message')}
                    </p>
                  </div>
                )}

                {/* AI Agent info */}
                <div className="flex items-center gap-3">
                  <ZapIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <p className="text-sm text-foreground">
                    {t('campaigns.review.dialog.ai_agent_message', {
                      0: draftAiAgentData?.brain_name || 'Unknown',
                    })}
                  </p>
                </div>

                {/* Monitor performance */}
                <div className="flex items-center gap-3">
                  <ChartNoAxesCombinedIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <p className="text-sm text-foreground">
                    {t('campaigns.review.dialog.monitor_message')}
                  </p>
                </div>
              </div>

              {/* Can still edit info */}
              <div className="rounded-lg border border-border p-4">
                <div className="flex items-start gap-3">
                  <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm leading-none font-medium">
                      {t('campaigns.review.dialog.can_edit_title')}
                    </p>
                    <p className="text-sm leading-6 text-muted-foreground">
                      {t('campaigns.review.dialog.can_edit_message')}
                    </p>
                  </div>
                </div>
              </div>
            </>
          </DialogDescription>

          <DialogFooter className="gap-3">
            <Button variant="ghost-neutral" onClick={() => setIsConfirmDialogOpen(false)}>
              {t('campaigns.review.dialog.cancel')}
            </Button>
            <Button onClick={handleConfirmSubmit}>
              {t('campaigns.review.dialog.start_campaign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
